import { useFormik } from 'formik';
import { Link } from 'react-router';
import * as Yup from 'yup';
import { APP } from '@/config';
import { ERRORS, ROUTES } from '@/constants';
import { ApiError } from '@/services/api/client';
import { useLogin } from '@/services/queries';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import type { MfaChallenge } from '@/types';
import { Button } from '@/shared/ui/button';
import { FormError, PasswordField, TextField } from './fields';
import { FOOTER, LINK, SUBMIT, SUBTITLE, TITLE } from './styles';

/**
 * The sign-in form.
 *
 * Worth reading as the pattern for every form in this codebase: Formik owns the
 * field state and submission, Yup states the rules, and a 422 from the server is
 * mapped back onto the fields that caused it.
 *
 * The server validates every request with Zod regardless. Yup here is for
 * immediate feedback, never a decision.
 *
 * Accounts are the `portal_users` rows an administrator creates; the password is
 * the one in `auth_credentials`, the same one the desktop app signs in with.
 */
const schema = Yup.object({
  email: Yup.string()
    .trim()
    .required('Enter your email address.')
    // Deliberately not a stricter pattern. Over-strict email rules reject valid
    // addresses - plus-addressing, new TLDs - and the server has the last word.
    .email('Enter a valid email address.')
    .max(320, 'That address is too long.'),
  password: Yup.string().required('Enter your password.'),
});

export function LoginForm({
  initialEmail = '',
  notice,
  onChallenge,
}: {
  /** Carried back from a completed reset, so the address is not typed twice. */
  initialEmail?: string;
  /** A one-line confirmation above the form, e.g. after a password reset. */
  notice?: string;
  /**
   * Password accepted, second factor outstanding. Handed upward because the
   * page swaps the whole form, footer included, for the code step.
   */
  onChallenge: (challenge: MfaChallenge) => void;
}) {
  const login = useLogin();

  const formik = useFormik({
    initialValues: { email: initialEmail, password: '' },
    validationSchema: schema,
    onSubmit: (values, helpers) => {
      login.mutate(
        { email: values.email.trim(), password: values.password },
        {
          onSuccess: (result) => {
            // A completed login navigates from inside useLogin; only the MFA
            // branch needs anything from here.
            if ('challengeId' in result) onChallenge(result);
          },
          onError: (error) => {
            // A 422 arrives keyed by request path - `body.email` - so it can be
            // shown against the input that caused it rather than as a banner.
            if (error instanceof ApiError && error.fieldErrors) {
              const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), values);
              helpers.setErrors(placed);
            }
          },
        },
      );
    },
  });

  const apiError = login.error instanceof ApiError ? login.error : null;

  /**
   * A banner for anything that is not about one field.
   *
   * Suppressed when field errors exist, because those are shown in place - but
   * only when at least one of them could be placed. An error with no input to
   * attach it to would otherwise fail completely silently.
   */
  const banner = (): string | null => {
    if (!apiError) return null;

    if (apiError.fieldErrors) {
      const { unplaced } = splitFormErrors(toFormErrors(apiError.fieldErrors), formik.values);
      return unplaced.length > 0 ? apiError.message : null;
    }

    if (apiError.code === 'invalid_credentials') return ERRORS.invalidCredentials;
    if (apiError.code === 'account_locked') return ERRORS.accountLocked;
    if (apiError.code === 'rate_limited') return ERRORS.rateLimited;
    return apiError.message;
  };

  /** Shown once touched, so the form does not scold somebody mid-typing. */
  const error = (field: 'email' | 'password'): string | undefined =>
    formik.touched[field] ? formik.errors[field] : undefined;

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
      <header>
        <h1 className={TITLE}>Sign in to {APP.name}</h1>
        <p className={SUBTITLE}>Welcome back. Enter your {APP.name} account details.</p>
      </header>

      {notice && !apiError && (
        <p
          role="status"
          className="rounded-md border border-success-500/30 bg-success-50 px-3 py-2 text-xs text-success-500"
        >
          {notice}
        </p>
      )}
      <FormError
        message={banner()}
        {...(apiError?.requestId ? { requestId: apiError.requestId } : {})}
      />

      <TextField
        id="email"
        label="Email"
        type="email"
        autoComplete="username"
        autoFocus={!initialEmail}
        placeholder="you@yourlab.com"
        {...formik.getFieldProps('email')}
        error={error('email')}
      />

      <PasswordField
        id="password"
        label="Password"
        autoComplete="current-password"
        autoFocus={Boolean(initialEmail)}
        labelAction={
          <Link
            to={ROUTES.resetPassword}
            // The address typed so far rides along, so the reset form starts
            // filled in rather than asking for it again.
            state={{ email: formik.values.email.trim() }}
            /*
             * Keeps focus where it is. Otherwise pressing this blurs an empty
             * email field, its "Enter your email" error appears, the link is
             * pushed down before the mouse comes up, and the click misses.
             */
            onMouseDown={(event) => event.preventDefault()}
            className={LINK}
          >
            Reset password
          </Link>
        }
        {...formik.getFieldProps('password')}
        error={error('password')}
      />

      <Button type="submit" className={SUBMIT} disabled={login.isPending}>
        {login.isPending ? 'Signing in…' : 'Sign in'}
      </Button>

      <p className={FOOTER}>
        No account yet? Your sign-in details are emailed to you when your licence is issued.
      </p>
    </form>
  );
}
